import {
  PageBlockDefinitionMultiRichTextPropertyStyle,
  PageBlockDefinitionMutiRichTextOptions,
  PageBlockDefinitionProperty,
  PageBlockText,
} from "@supernova-studio/model";
import { PageBlockEditorModel } from "./model";
import { BlockParsingUtils } from "./utils";

type Node = ListNode | ListItemNode;

export type ListNode = {
  type: "List";
  listType: "Ordered" | "Unordered";
  block: PageBlockEditorModel;
  leadingChildren: ListItemNode[];
  children: Node[];
};

export type ListItemNode = {
  type: "ListItem";
  text: PageBlockText;
};

export class ListTreeBuilder {
  private rootNode: ListNode | undefined;

  addWithProperty(
    block: PageBlockEditorModel,
    multiRichTextProperty: PageBlockDefinitionProperty
  ): ListNode | undefined {
    const parsedOptions = PageBlockDefinitionMutiRichTextOptions.optional().parse(multiRichTextProperty.options);
    return this.add(block, multiRichTextProperty.id, parsedOptions?.multiRichTextStyle || "OL");
  }

  add(
    block: PageBlockEditorModel,
    multiRichTextPropertyId: string,
    multiRichTextPropertyStyle: PageBlockDefinitionMultiRichTextPropertyStyle
  ) {
    const list = this.createList(block, multiRichTextPropertyId, multiRichTextPropertyStyle);

    // No root
    if (!this.rootNode) {
      this.rootNode = list;
      return undefined;
    }

    // Inserting at the root
    if (block.data.indentLevel <= 0) {
      const flushed = this.flush();
      this.rootNode = list;
      return flushed;
    }

    // Inserting at a depth
    const listParent = this.getParentOfDepth(block.data.indentLevel);

    const lastChild = listParent.children[listParent.children.length - 1];
    if (lastChild?.type === "List") {
      // Merge items into the current list
      lastChild.children.push(...list.leadingChildren);
      return;
    } else {
      // We can add this list as a child of another list
      listParent.children.push(list);
    }
  }

  flush(): ListNode | undefined {
    const node = this.rootNode;
    this.rootNode = undefined;
    return node;
  }

  private getParentOfDepth(depth: number): ListNode {
    if (!this.rootNode || depth <= 0) throw new Error("Invalid state");

    let currentNode = this.rootNode;
    let currentDepth = depth - 1;

    while (currentDepth > 0) {
      // Check if we can go any lower
      if (currentNode.children.length === 0) return currentNode;

      const lastChild = currentNode.children[currentNode.children.length - 1];
      if (lastChild.type !== "List") {
        // Cannot go lower if last child is not a list
        return currentNode;
      }

      currentNode = lastChild;
      currentDepth--;
    }

    return currentNode;
  }

  private createList(
    block: PageBlockEditorModel,
    multiRichTextPropertyId: string,
    multiRichTextPropertyStyle: PageBlockDefinitionMultiRichTextPropertyStyle
  ): ListNode {
    // private createList(block: PageBlockEditorModel, multiRichTextProperty: PageBlockDefinitionProperty): ListNode {
    const blockItem = BlockParsingUtils.singleBlockItem(block);
    const multiRichTextValue = BlockParsingUtils.multiRichTextPropertyValue(blockItem, multiRichTextPropertyId);

    const leadingChildren: ListNode["leadingChildren"] = multiRichTextValue.value.map(t => {
      return {
        type: "ListItem",
        text: t,
      };
    });

    if (!leadingChildren.length) {
      leadingChildren.push({ type: "ListItem", text: { spans: [] } });
    }

    return {
      type: "List",
      listType: multiRichTextPropertyStyle === "OL" ? "Ordered" : "Unordered",
      leadingChildren: leadingChildren,
      block: block,
      children: [],
    };
  }
}
